type BlockNavigation = (href: string, resume: () => void) => void;
const STATE_KEY = "__klapHistory";
type Position = { documentId: string; index: number };
type GuardWindow = Window & {
  __klapHistoryDispatcherInstalled?: boolean;
  __klapHistoryPopstate?: (event: PopStateEvent) => void;
};

/** Window-targeted popstate listeners run in registration order, even with
 * capture enabled. This dispatcher must be installed before Next hydrates. */
export function installHistoryPopstateDispatcher(win: GuardWindow) {
  if (win.__klapHistoryDispatcherInstalled) return;
  win.__klapHistoryDispatcherInstalled = true;
  win.addEventListener("popstate", event => win.__klapHistoryPopstate?.(event), true);
}

// The bootstrap is self-contained; no module bindings are referenced inside it.
export const HISTORY_GUARD_BOOTSTRAP = `(${installHistoryPopstateDispatcher.toString()})(window);`;

const trackers = new WeakMap<Window, ReturnType<typeof createHistoryNavigationGuard>>();

/** Install before entering Schedule so both directions already have positions.
 * Preserve Next's history state and stop popstate before its router unmounts
 * the editor. Cancel restores the original entry; Discard traverses the real
 * history stack rather than pushing a replacement URL. */
export function createHistoryNavigationGuard(win: Window) {
  installHistoryPopstateDispatcher(win);
  const history = win.history;
  const documentId = win.crypto.randomUUID();
  let index = 0;
  let block: BlockNavigation | null = null;
  let allowedIndex: number | null = null;
  let restoring: { destination: number; href: string; notify: BlockNavigation } | null = null;
  const push = history.pushState.bind(history);
  const replace = history.replaceState.bind(history);
  const stateAt = (state: unknown, at: number) => ({
    ...(state && typeof state === "object" ? state : {}),
    [STATE_KEY]: { documentId, index: at },
  });
  replace(stateAt(history.state, index), "");
  history.pushState = (state, title, url) => {
    push(stateAt(state, index + 1), title, url);
    index += 1;
  };
  history.replaceState = (state, title, url) => replace(stateAt(state, index), title, url);

  const onPopState = (event: PopStateEvent) => {
    const position = event.state?.[STATE_KEY] as Position | undefined;
    // Cross-document traversal is protected by the native beforeunload guard.
    if (!position || position.documentId !== documentId) return;
    const destination = position.index;
    if (restoring) {
      event.stopImmediatePropagation();
      if (destination !== index) {
        history.go(index - destination);
        return;
      }
      const pending = restoring;
      restoring = null;
      pending.notify(pending.href, () => {
        allowedIndex = pending.destination;
        history.go(pending.destination - index);
      });
      return;
    }
    if (destination === allowedIndex) {
      allowedIndex = null;
      index = destination;
      return;
    }
    if (!block || destination === index) {
      index = destination;
      return;
    }
    event.stopImmediatePropagation();
    restoring = { destination, href: win.location.href, notify: block };
    history.go(index - destination);
  };
  (win as GuardWindow).__klapHistoryPopstate = onPopState;
  return {
    block(handler: BlockNavigation) {
      block = handler;
      return () => { if (block === handler) block = null; };
    },
  };
}

export function getHistoryNavigationGuard(win: Window) {
  let tracker = trackers.get(win);
  if (!tracker) {
    tracker = createHistoryNavigationGuard(win);
    trackers.set(win, tracker);
  }
  return tracker;
}
